import {
  ACTION_BUSINESS_CLICK_SEARCH_MODAL_BOOK,
  ACTION_BUSINESS_SELECT_BOOKING_CARD_CLOSE,
  ACTION_BUSINESS_SELECT_BOOKING_CARD_OPEN,
  EventCustomAttributes,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { BookingCardContent } from 'components/business/search/booking-card/BookingCardContent';
import { BookingCardLeftContent } from 'components/business/search/booking-card/BookingCardLeftContent';
import { BookingCardSeatingAreaButton } from 'components/business/search/booking-card/BookingCardSeatingAreaButton';
import { BookingCardTimeDetails } from 'components/business/search/booking-card/BookingCardTimeDetails';
import { Collapse } from '@material-ui/core';
import { DateTimeFormatter, LocalDateTime } from 'js-joda';
import { ExclusivityEntry, LockOptions } from 'types';
import { FC, useContext, useState } from 'react';
import { Stats } from 'utils/calendar';
import { Theme } from '@emotion/react';

import { IconBell } from '@tock/icons';
import { LocaleContext, consumer, core } from '@tock/coretypes';
import { experienceOfferingToData } from 'utils/analytics/amplitude';
import { getHasNonZeroEffectivePrice } from 'utils/priceDisplayUtils';
import { getTimeFormatter } from '@tock/i18n';
import { secondaryContainer } from 'omakase/Collapse/collapseCssDefs';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useConsumerConversionAnalytics } from 'hooks/analytics/useConsumerConversionAnalytics';
import { useExperimentVariant } from 'hooks/useExperiment';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useHistory } from 'react-router';
import HideFromIOSApp from 'components/ui/HideFromIOSApp';
import clsx from 'clsx';
import queryString from '@tock/query-string';

function getCommunalCountText(
  ticketCount: number,
  maxCallout: number,
  neverShowSeatsRemaining: boolean
): string {
  if (neverShowSeatsRemaining || ticketCount <= 0 || ticketCount > maxCallout) {
    return '';
  } else {
    // The narrow case.
    return `${ticketCount} remaining`;
  }
}

export interface Props {
  communalInfo?: {
    ticketCount: number;
    maxCallout: number;
  };
  experience: consumer.IOffering | undefined;
  communalTicketCount?: number;
  exclusivityEntry?: ExclusivityEntry;
  isAvailable?: boolean;
  isNotAvailableToCurrentUser?: boolean;
  isRescheduling?: boolean;
  isShipping: boolean;
  isSoldOutTime?: boolean;
  onClick(dateTime: LocalDateTime, priceCents: number, options: LockOptions): void;
  partySize: number;
  priceInfo: Stats;
  purchaseSize: number;
  reschedulePriceInfo?: Stats;
  seatingAreas: core.IAvailabilitySeatingArea[];
  showExclusive?: boolean;
  showWaitlist?: boolean;
  singleUnitQuantity?: number;
  time: string | null;
  userLockedDateTime?: string;
  userLockedSeatingArea?: core.SeatingArea;
  waitlistUrl?: string;
  soldOutSeatingAreaIds?: number[];
  hidePrice?: boolean;
  businessId?: number;
}

export const BookingCard: FC<Props> = ({
  communalInfo: { ticketCount, maxCallout } = {
    ticketCount: 0,
    maxCallout: 0,
  },
  exclusivityEntry,
  experience,
  isAvailable,
  isNotAvailableToCurrentUser,
  isRescheduling,
  isShipping,
  isSoldOutTime,
  onClick,
  partySize,
  priceInfo,
  purchaseSize,
  reschedulePriceInfo,
  seatingAreas,
  showExclusive = false,
  showWaitlist = false,
  singleUnitQuantity = 1,
  time,
  userLockedDateTime,
  userLockedSeatingArea,
  waitlistUrl,
  soldOutSeatingAreaIds = [],
  hidePrice,
  businessId,
}) => {
  const locale = useContext(LocaleContext);
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const sendConversionAnalytics = useConsumerConversionAnalytics();
  const history = useHistory();
  const neverShowSeatsRemaining = useFeatureFlag('NEVER_SHOW_SEATS_REMAINING');
  const disableExperiment = useFeatureFlag('CONSUMER_DISABLE_DEPOSIT_EXPERIMENTS');
  const hidePriceExperiment =
    useExperimentVariant('DepositDemandExperiment') === 1 && !disableExperiment;
  const [isExpanded, setIsExpanded] = useState(false);

  /**
   * If there is no time, then no need to continue
   */
  if (!time) {
    return null;
  }
  const parsedTime = LocalDateTime.parse(time);

  const handleToggleExpand = (event: React.MouseEvent<HTMLDivElement>) => {
    // Order matters for visual timing of changes
    setIsExpanded(!isExpanded);
    event.currentTarget.parentElement?.classList.toggle('is-open');

    const analyticsData: EventCustomAttributes = {
      ...experienceOfferingToData(experience),
      reservationDateTime: parsedTime.format(DateTimeFormatter.ofPattern('yyyy-MM-dd hh:mm:ss')),
      partySize,
    };

    amplitudeSendEvent(
      isExpanded
        ? ACTION_BUSINESS_SELECT_BOOKING_CARD_CLOSE
        : ACTION_BUSINESS_SELECT_BOOKING_CARD_OPEN,
      analyticsData
    );
  };

  // From the typing info, priceInfo.priceRange should be defined and not null,
  // but per a production issue -- https://tock.atlassian.net/browse/TK-7893 -- a null
  // can actually sneak through.
  const baseTicketPrice = priceInfo.priceRange !== null ? (priceInfo.priceRange?.max ?? 0) : 0;

  const hasNonZeroEffectivePrice = getHasNonZeroEffectivePrice(
    baseTicketPrice,
    priceInfo?.minRequiredSpend?.totalCents ?? 0,
    priceInfo.priceDisplayStyle
  );

  const renderPrice =
    !hidePrice &&
    !hidePriceExperiment &&
    hasNonZeroEffectivePrice &&
    purchaseSize === partySize &&
    singleUnitQuantity === 1;

  const matchesUserLockedDateTime = !!userLockedDateTime && time === userLockedDateTime;
  const disabled = isRescheduling ? !isAvailable : !isAvailable && !matchesUserLockedDateTime;
  const showNotifyLink = disabled && showWaitlist;

  /**
   * Handle the act of booking a time
   */
  const handleBooking = (seatingAreaId: number | null) => {
    const options: LockOptions = {
      showError: true,
      exclusivity: exclusivityEntry?.exclusivity,
    };

    if (seatingAreaId) {
      options.seatingAreaId = seatingAreaId;
    }

    onClick(parsedTime, priceInfo.priceRange.max, options);
    sendConversionAnalytics(ACTION_BUSINESS_CLICK_SEARCH_MODAL_BOOK);
  };

  /**
   * See if the any current user locks involving a seating area matches an offered seating area
   */
  const matchesPossibleLockedSeatingArea = (seatingAreaId?: number) =>
    (!!seatingAreaId && !!userLockedSeatingArea && userLockedSeatingArea.id === seatingAreaId) ||
    seatingAreaId === undefined;

  /**
   * Check if a time is locked by the current user
   */
  const isLockedByCurrentUser = (seatingAreaId?: number) =>
    matchesUserLockedDateTime && matchesPossibleLockedSeatingArea(seatingAreaId);

  /**
   * Does a seating area accommodate a party size
   */
  const canAccommodatesPartySize = (seatingArea: core.IAvailabilitySeatingArea) =>
    !!seatingArea?.maxPartySize &&
    !!seatingArea?.minPartySize &&
    seatingArea.maxPartySize >= partySize &&
    seatingArea.minPartySize <= partySize;

  /**
   * Check if a seating area is sold out
   */
  const isSoldOutSeatingArea = (seatingAreaId: number | null | undefined) =>
    seatingAreaId ? soldOutSeatingAreaIds.includes(seatingAreaId) : false;
  const hasSeatingAreaName = (seatingArea: core.IAvailabilitySeatingArea) => !!seatingArea.name;

  /**
   * Get the status of the current booking card
   */
  const getCardStatus = (seatingAreaId?: number) => {
    const lockedByCurUser = isLockedByCurrentUser(seatingAreaId);

    if (disabled || isNotAvailableToCurrentUser) {
      return 'DISABLED';
    } else if (isSoldOutSeatingArea(seatingAreaId)) {
      return 'SOLD_OUT';
    } else if (lockedByCurUser) {
      return 'LOCKED_BY_USER';
    }

    return 'AVAILABLE';
  };

  /**
   * Get class names to define the status of buttons or elements that act as buttons
   */
  const genClassNameForAction = (seatingAreaId?: number) => {
    const lockedByCurUser = isLockedByCurrentUser(seatingAreaId);

    return clsx('Consumer-resultsBookingCardItem', {
      'is-disabled': disabled,
      'is-available': isAvailable || lockedByCurUser,
      'is-locked': isNotAvailableToCurrentUser,
      'is-sold-out': isSoldOutSeatingArea(seatingAreaId),
      'is-locked-date-time': lockedByCurUser,
    });
  };

  const getPartySizeLimitsText = (seatingArea: core.IAvailabilitySeatingArea) => {
    const prefix = 'Parties of';
    const { minPartySize: min, maxPartySize: max } = seatingArea;

    if (min && max) {
      return min === max ? `${prefix} ${min}` : `${prefix} ${min}-${max}`;
    } else if (min) {
      return `${prefix} ${min}`;
    } else if (max) {
      return `${prefix} ${max}`;
    }
    return '';
  };

  // Seating areas that have not sold out
  const nonSoldOutSeatingAreas = seatingAreas.filter(
    (s) => hasSeatingAreaName(s) && !isSoldOutSeatingArea(s.id)
  );

  // We want to show seating areas that can accommodate the selected party size and that aren't sold out
  const availableSeatingAreasToShow = nonSoldOutSeatingAreas.filter(canAccommodatesPartySize);

  // We want to show seating areas that cannot accommodate the selected party size as disabled
  // so the user knows changing the party size might enable more options
  const unavailableSeatingAreasToShow = seatingAreas.filter(
    (s) => hasSeatingAreaName(s) && !canAccommodatesPartySize(s)
  );

  const getTimeSlotDetails = (useLightTheme: boolean) => (
    <BookingCardTimeDetails
      priceInfo={priceInfo}
      purchaseSize={purchaseSize}
      renderPrice={renderPrice}
      communalCountText={getCommunalCountText(ticketCount, maxCallout, neverShowSeatsRemaining)}
      isAvailable={!!isAvailable}
      isNotAvailableToCurrentUser={!!isNotAvailableToCurrentUser}
      reschedulePriceInfo={reschedulePriceInfo}
      useLightTheme={useLightTheme}
    />
  );

  const leftContent = (
    <BookingCardLeftContent
      isShipping={isShipping}
      time={time}
      showNotifyLink={showNotifyLink}
      waitlistUrl={waitlistUrl || ''}
      isAvailable={!!isAvailable}
      matchesUserLockedDateTime={matchesUserLockedDateTime}
      soldOutCondition={!!(isSoldOutTime || isNotAvailableToCurrentUser)}
      isRescheduling={!!isRescheduling}
      seatingAreas={!isExpanded ? availableSeatingAreasToShow : []}
      helperText={isExpanded ? 'Select a seating option' : ''}
    />
  );

  const bookingCardTimeLabel = getTimeFormatter(locale).format(parsedTime);
  const effectiveTicketTypeName = experience?.name || 'Reservation';

  /**
   * Show option to join waitlist
   */
  if (showNotifyLink && waitlistUrl) {
    const notifyButtonAriaLabel = `Join Notify list for ${effectiveTicketTypeName} at ${bookingCardTimeLabel}`;
    return (
      <HideFromIOSApp additionalConditional={false}>
        <BookingCardContent
          handleClick={(event) => {
            const timeFormatter = DateTimeFormatter.ofPattern('HH:mm');
            const desiredTime = parsedTime.format(timeFormatter);
            history.push(`${waitlistUrl}&${queryString.stringify({ time: desiredTime })}`);
          }}
          bookingCardStatus="SOLD_OUT"
          button-aria-label={notifyButtonAriaLabel}
          mainContent={
            <>
              {leftContent}
              {getTimeSlotDetails(false)}
            </>
          }
          buttonLabel="Notify"
          buttonIcon={<IconBell />}
        />
      </HideFromIOSApp>
    );
  }

  /**
   * Start of showing seating options choices
   */
  const buttonLabel =
    isAvailable && isLockedByCurrentUser()
      ? 'Continue'
      : isSoldOutTime || isNotAvailableToCurrentUser
        ? 'Sold out'
        : isShipping
          ? 'Order'
          : 'Book';
  const buttonAriaLabel = `${buttonLabel} ${effectiveTicketTypeName} at ${bookingCardTimeLabel}`;

  /**
   * Show a single non-expandable component for 0-1 seating areas
   */
  if (nonSoldOutSeatingAreas.length < 2) {
    const possibleSeatingAreaId = nonSoldOutSeatingAreas[0]?.id ?? null;

    return (
      <HideFromIOSApp additionalConditional={false}>
        <BookingCardContent
          bookingCardStatus={getCardStatus()}
          button-aria-label={buttonAriaLabel}
          buttonLabel={buttonLabel}
          handleClick={() => handleBooking(possibleSeatingAreaId)}
          isDisabled={isSoldOutTime || isNotAvailableToCurrentUser}
          mainContent={
            <>
              {leftContent}
              {getTimeSlotDetails(false)}
            </>
          }
        ></BookingCardContent>
      </HideFromIOSApp>
    );
  }

  /**
   * Show a custom expandable component for 2+ seating areas
   */
  const renderBookingCardSeatingAreaButtons = (
    seatingArea: core.IAvailabilitySeatingArea,
    isDisabled: boolean,
    isFirst?: boolean
  ) => {
    const seatingAreaName = seatingArea.name;
    const seatingAreaLabelPrefix = isDisabled
      ? 'Unavailable seating area'
      : 'Select a seating area';
    const seatingAreaButtonAriaLabel = `${seatingAreaLabelPrefix}: ${seatingAreaName} for ${effectiveTicketTypeName} at ${bookingCardTimeLabel}`;

    return (
      <BookingCardSeatingAreaButton
        key={seatingArea.id}
        aria-label={seatingAreaButtonAriaLabel}
        className={genClassNameForAction(seatingArea?.id ?? undefined)}
        isDisabled={isDisabled}
        isFocused={isFirst}
        onClick={() => handleBooking(seatingArea.id ?? null)}
        seatingArea={seatingArea}
      >
        <span>{seatingAreaName}</span>
        {canAccommodatesPartySize(seatingArea)
          ? getTimeSlotDetails(true)
          : getPartySizeLimitsText(seatingArea)}
      </BookingCardSeatingAreaButton>
    );
  };

  return (
    <HideFromIOSApp additionalConditional={false}>
      <BookingCardContent
        bookingCardStatus={getCardStatus()}
        buttonHidden={isExpanded}
        button-aria-label={buttonAriaLabel}
        buttonLabel={buttonLabel}
        handleClick={handleToggleExpand}
        mainContent={leftContent}
      >
        <Collapse in={isExpanded} timeout="auto">
          <section css={(theme: Theme) => [secondaryContainer, { padding: theme.spacing(2) }]}>
            {availableSeatingAreasToShow.map((seatingArea, idx) => {
              return renderBookingCardSeatingAreaButtons(
                seatingArea,
                false,
                idx === 0 && isExpanded
              );
            })}
            {unavailableSeatingAreasToShow.map((seatingArea) => {
              return renderBookingCardSeatingAreaButtons(seatingArea, true);
            })}
          </section>
        </Collapse>
      </BookingCardContent>
    </HideFromIOSApp>
  );
};
